import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyMigration,
  CutoverResult,
  DryRunReport,
  getMigrationApplyStatus,
  getMigrationMapping,
  runMigrationDryRun,
  saveMigrationMapping,
} from '@/api/migrationService';
import { getPlans } from '@/api/plansService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

/** PRD 12 §3 — the mapping screen's data: V1 plans, the V2 plans to choose from, and the dry run. */
export const useMigration = () => {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [report, setReport] = useState<DryRunReport | null>(null);
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<CutoverResult | null>(null);
  const wasRunning = useRef(false);

  const { data: mapping, isLoading } = useQuery({
    queryKey: queryKeys.migrationMapping,
    queryFn: getMigrationMapping,
  });

  // Every published V2 plan is a candidate target, whatever its type: a base plan maps to a generalist
  // and a beginner plan to a reprise, so filtering by type here would hide the right answer.
  const { data: targets } = useQuery({
    queryKey: queryKeys.pv2Plans({ page: 1 }),
    queryFn: () => getPlans({ page: 1, pageSize: 200 }),
  });

  useEffect(() => {
    if (mapping) setValues(Object.fromEntries(mapping.plans.map((p) => [p.planId, p.v2PlanId ?? ''])));
  }, [mapping]);

  const save = useMutation({
    mutationFn: () =>
      saveMigrationMapping(Object.entries(values).map(([planId, v2PlanId]) => ({ planId, v2PlanId: v2PlanId || null }))),
    onSuccess: (payload) => {
      queryClient.setQueryData(queryKeys.migrationMapping, payload);
      toaster.create({ title: 'Correspondances enregistrées', type: 'success' });
    },
    onError: () => toaster.create({ title: 'Enregistrement impossible', type: 'error' }),
  });

  const dryRun = useMutation({
    mutationFn: runMigrationDryRun,
    onSuccess: setReport,
    onError: () => toaster.create({ title: 'La vérification a échoué', type: 'error' }),
  });

  const { data: status } = useQuery({
    queryKey: queryKeys.migrationApplyStatus,
    queryFn: getMigrationApplyStatus,
    refetchInterval: (query) => (query.state.data?.running ? 3000 : false),
  });

  const onFinished = (done: CutoverResult) => {
    setResult(done);
    queryClient.invalidateQueries({ queryKey: queryKeys.migrationMapping });
    toaster.create({
      title: `Migration terminée : ${done.migrated} migré(s) · ${done.skipped} ignoré(s) · ${done.failed} échec(s)`,
      type: done.failed > 0 ? 'warning' : 'success',
    });
  };

  useEffect(() => {
    if (!status) return;
    if (wasRunning.current && !status.running) {
      if (status.result) onFinished(status.result);
      else if (status.error) toaster.create({ title: `La migration a échoué : ${status.error}`, type: 'error' });
    }
    wasRunning.current = status.running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const apply = useMutation({
    mutationFn: () => applyMigration(email.trim() || undefined),
    onSuccess: (payload) => {
      if ('running' in payload) {
        setResult(null);
        queryClient.setQueryData(queryKeys.migrationApplyStatus, payload);
      } else {
        onFinished(payload);
      }
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      toaster.create({ title: error.response?.data?.message ?? 'La migration a échoué', type: 'error' }),
  });

  const dirty = Boolean(
    mapping && mapping.plans.some((p) => (values[p.planId] ?? '') !== (p.v2PlanId ?? ''))
  );

  return {
    mapping,
    isLoading,
    targets: targets?.items ?? [],
    values,
    set: (planId: string, v2PlanId: string) => setValues((v) => ({ ...v, [planId]: v2PlanId })),
    dirty,
    save,
    dryRun,
    report,
    email,
    setEmail,
    apply,
    running: Boolean(status?.running),
    result,
  };
};
